import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, BadgeCheck } from 'lucide-react';
import { fetchProducts } from '../lib/api';

const BUYERS = [
  ['Jasmine', 'Houston, TX'], ['Aaliyah', 'Atlanta, GA'], ['Destiny', 'Chicago, IL'],
  ['Imani', 'Memphis, TN'], ['Nia', 'Brooklyn, NY'], ['Camille', 'Detroit, MI'],
  ['Zoe', 'Oakland, CA'], ['Maya', 'Newark, NJ'], ['Tiana', 'Baltimore, MD'],
  ['Simone', 'Cincinnati, OH'], ['Brianna', 'Charlotte, NC'], ['Kennedy', 'St. Louis, MO'],
  ['Sydney', 'Columbus, OH'], ['Alicia', 'Miami, FL'], ['Robin', 'Dallas, TX'],
  ['Whitney', 'Seattle, WA'], ['Erica', 'Phoenix, AZ'], ['Paige', 'Denver, CO'],
];

const HIDDEN_PREFIXES = ['/admin', '/checkout', '/payment'];
const FIRST_DELAY = 8000;
const SHOW_FOR = 6500;
const INTERVAL = 20000;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const SalesPopup = () => {
  const { pathname } = useLocation();
  const [products, setProducts] = useState([]);
  const [popup, setPopup] = useState(null);
  const timers = useRef([]);
  const queues = useRef({ hair: [], acc: [], buyers: [], count: 0 });

  useEffect(() => {
    let alive = true;
    fetchProducts()
      .then((list) => { if (alive) setProducts(list.filter((p) => !p.coming_soon)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!products.length) return undefined;
    const q = queues.current;
    const next = (key, source) => {
      if (!q[key].length) q[key] = shuffle(source);
      return q[key].pop();
    };
    const fire = () => {
      const accs = products.filter((p) => p.category === 'accessories');
      const hair = products.filter((p) => p.category !== 'accessories');
      // Every 3rd pop-up spotlights an accessory; queues never repeat until exhausted.
      const useAcc = accs.length && (q.count % 3 === 2 || !hair.length);
      const p = useAcc ? next('acc', accs) : next('hair', hair.length ? hair : products);
      q.count += 1;
      const [name, city] = next('buyers', BUYERS);
      const mins = 2 + Math.floor(Math.random() * 27);
      setPopup({ p, name, city, mins, key: Date.now() });
      timers.current.push(setTimeout(() => setPopup(null), SHOW_FOR));
    };
    timers.current.push(setTimeout(fire, FIRST_DELAY));
    const iv = setInterval(fire, INTERVAL);
    return () => { clearInterval(iv); timers.current.forEach(clearTimeout); timers.current = []; };
  }, [products]);

  if (HIDDEN_PREFIXES.some((pre) => pathname.startsWith(pre))) return null;
  const onProductPage = pathname.startsWith('/product/');

  return (
    <AnimatePresence>
      {popup && (
        <motion.div
          key={popup.key}
          initial={{ opacity: 0, y: 24, x: -12 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed left-4 sm:left-5 z-[70] ${onProductPage ? 'bottom-28' : 'bottom-5'} w-[300px] sm:w-[330px]`}
          data-testid="sales-popup"
        >
          <div className="relative flex items-center gap-3 bg-[#0f0e0c]/95 backdrop-blur border border-[#c6a15b]/40 shadow-[0_8px_30px_rgba(0,0,0,0.45)] p-3 pr-8">
            <Link to={`/product/${popup.p.id}`} className="shrink-0" data-testid="sales-popup-image-link">
              <img src={popup.p.image} alt={popup.p.name} className={`w-14 h-14 ${popup.p.image_fit === 'contain' ? 'object-contain bg-[#f2ece1]' : 'object-cover'} border border-[#c6a15b]/30`} />
            </Link>
            <div className="min-w-0">
              <p className="font-body text-[0.8rem] text-[#e7e0d3] leading-snug">
                <span className="text-[#c6a15b] font-semibold">{popup.name}</span> from {popup.city} just bought
              </p>
              <Link to={`/product/${popup.p.id}`} className="block font-body text-[0.8rem] font-semibold text-[#f7f3ec] truncate hover:text-[#c6a15b] transition-colors" data-testid="sales-popup-product-link">
                {popup.p.name}
              </Link>
              <p className="flex items-center gap-1 text-[0.65rem] text-[#9c9587] mt-0.5">
                <BadgeCheck size={12} className="text-[#c6a15b]" /> Verified purchase · {popup.mins} min ago
              </p>
            </div>
            <button
              onClick={() => setPopup(null)}
              aria-label="Dismiss"
              data-testid="sales-popup-close"
              className="absolute top-1.5 right-1.5 text-[#9c9587] hover:text-[#f7f3ec] transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SalesPopup;
